import React, { useState, useRef } from "react";
import { Checkbox, Button, Popover, Badge } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import "./ColumnFilter.scss";

/**
 * Modern Column Filter Component with Checkboxes
 * Supports multiple selection with a clean popover design
 * Similar to Status filter in MerchantFilters component
 *
 * @param {string} label - Filter label (e.g., "Company")
 * @param {Array} options - Array of filter options
 * @param {Array} selectedValues - Currently selected values
 * @param {Function} onChange - Callback when selection changes
 */
const ColumnFilter = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  loading = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [placement, setPlacement] = useState("topRight");
  const filterRef = useRef(null);

  // Filter options based on search
  const filteredOptions = options.filter((option) => {
    // Handle both object {value, label} and string formats
    const searchableText =
      typeof option === "object"
        ? String(option.label || option.value || "")
        : String(option || "");
    return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handle checkbox change
  const handleCheckboxChange = (value, checked) => {
    let newValues;
    if (checked) {
      newValues = [...selectedValues, value];
    } else {
      newValues = selectedValues.filter((v) => v !== value);
    }
    onChange(newValues);
  };

  // Select all
  const handleSelectAll = () => {
    const allValues = options.map((opt) =>
      opt.value !== undefined ? opt.value : opt
    );
    onChange(allValues);
  };

  // Clear all
  const handleClearAll = () => {
    onChange([]);
  };

  // Check if all are selected
  const isAllSelected = selectedValues.length === options.length;

  // Dynamically adjust placement based on available space
  const handleVisibleChange = (newVisible) => {
    if (newVisible && filterRef.current) {
      const rect = filterRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 400; // max-height of popover

      // If not enough space below but enough above, open upward
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setPlacement("topRight ");
      } else {
        setPlacement("bottomRight");
      }
    }
    setVisible(newVisible);
  };

  // Popover content
  const filterContent = (
    <div className="column-filter-popover">
      {/* Search input at the top - always show */}
      <div className="filter-search">
        <input
          type="text"
          placeholder={`Search ${label.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-search-input"
          onClick={(e) => e.stopPropagation()}
        />
        {searchTerm ? (
          <button
            type="button"
            className="clear-search-btn"
            onClick={() => setSearchTerm("")}
            title="Clear search"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        ) : (
          <svg
            className="search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        )}
      </div>

      {/* Filter options with checkboxes */}
      <div className="filter-options">
        {/* Show loading state */}
        {loading ? (
          <div className="no-options">
            <div className="loading-spinner"></div>
            <p>Loading {label.toLowerCase()}...</p>
          </div>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            // Handle both object {value, label} and string formats
            const isObject = typeof option === "object" && option !== null;
            const optionValue = isObject ? option.value : option;
            const optionLabel = isObject
              ? option.label || String(option.value)
              : String(option);
            const isChecked = selectedValues.includes(optionValue);

            return (
              <div
                key={`${optionValue}-${index}`}
                className={`filter-option ${isChecked ? "checked" : ""}`}
                onClick={() => handleCheckboxChange(optionValue, !isChecked)}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={(e) =>
                    handleCheckboxChange(optionValue, e.target.checked)
                  }
                >
                  {optionLabel}
                </Checkbox>
              </div>
            );
          })
        ) : (
          <div className="no-options">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <p>No options found</p>
            <small>Try a different search term</small>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="filter-actions">
        <Button
          size="small"
          onClick={handleSelectAll}
          disabled={isAllSelected}
          className="select-all-btn"
        >
          Select All
        </Button>
        <Button
          size="small"
          onClick={handleClearAll}
          disabled={selectedValues.length === 0}
          danger
          className="clear-all-btn"
        >
          Clear
        </Button>
      </div>
    </div>
  );

  return (
    <div className="modern-column-filter" ref={filterRef}>
      <span className="filter-label">{label}</span>
      <Popover
        content={filterContent}
        trigger="click"
        open={visible}
        onOpenChange={handleVisibleChange}
        placement={placement}
        overlayClassName="column-filter-popover-overlay"
      >
        <Badge
          count={selectedValues.length}
          offset={[-2, 2]}
          color="#52c41a"
          size="small"
        >
          <Button
            className={`filter-trigger-icon ${selectedValues.length > 0 ? "active" : ""
              }`}
            icon={<FilterOutlined />}
            size="small"
            shape="circle"
          />
        </Badge>
      </Popover>
    </div>
  );
};

export default ColumnFilter;
